/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FieldType, KngButtonPosition, KngButtonSize, KngButtonState, KngButtonStyle, KngCardListState, KngHeaderObjState, KngValueObjState, SortOrder } from 'kinetic-angular';
import { RoutingFunction, StatusValueToRedirectsMap } from '../../components/auth';
import { CommonErrorService } from '../../components/common-error';
import { UserRegistrationStatus, UserRegistrationStatusValue } from '../api/definitions';
import { ApplicationPath, CountryCode, ErrorMessage, FieldName, FormattingName, Icons, IsRequestSubmittedForReview, MaxDeviceWidth, MyQuotesStatusHeader, OrderStatus, RemovalReasons } from './shared-enums';
import { Image } from './shared-interface';
import { InvoiceStatusHeader } from '../../components/order-management/definitions/order-management.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoggerService } from '../logger/logger.service';
import { BehaviorSubject } from 'rxjs';
import { accountManagerRoleId, tmhCorporateAdminRoleId, tmhCustomerSupportRoleId } from './shared-constants';
import { StatusPayload } from '../../components/my-quotes';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class SharedUtilService
{
	isAccountManagerCredential: boolean = false;
	constructor(private readonly errorService: CommonErrorService, private readonly localStorageService: LocalStorageService, private readonly loggerService: LoggerService,
		private readonly router: Router, private translate: TranslateService ) { }
	private readonly isTmhCorporateAdmin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	private readonly isTmhCustomerSupportRole$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public isCustomerSupportSession$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	get isTmhCorporateAdmin(): boolean
	{
		return this.isTmhCorporateAdmin$.value;
	}
	set isTmhCorporateAdmin(value: boolean)
	{
		this.isTmhCorporateAdmin$.next(value);
	}
	get isTmhCustomerSupportRole(): boolean
	{
		return this.isTmhCustomerSupportRole$.value;
	}
	set isTmhCustomerSupportRole(value: boolean)
	{
		this.isTmhCustomerSupportRole$.next(value);
	}
	get isCustomerSupportSession(): boolean
	{
		return this.isCustomerSupportSession$.value;
	}
	private readonly isEquipmentDetailsRedirectedFromMyInsights$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	get isEquipmentDetailsRedirectedFromMyInsights(): boolean
	{
		return this.isEquipmentDetailsRedirectedFromMyInsights$.value;
	}
	set isEquipmentDetailsRedirectedFromMyInsights(value: boolean)
	{
		this.isEquipmentDetailsRedirectedFromMyInsights$.next(value);
	}
	removeReasons: string[] = [ RemovalReasons.scrapped, RemovalReasons.stolen ];
	commentRequiredRemoveReasons: string[] = [ RemovalReasons.sold, RemovalReasons.exported ];
	private readonly fieldMap = new Map<string, string>([
		[ FieldName.truckNumber, FieldType.link ],
		[ FieldName.equipmentVin, FieldType.link ],
		[ FieldName.orderDate, FieldType.date ],
		[ FieldName.eta, FieldType.date ],
		[ FieldName.status, FieldType.orderStatus ],
		[ FieldName.equipmentStatus, FieldType.orderStatus ],
		[ FieldName.serviceDate, FieldType.date ],
		[ FieldName.workOrderNumber, FieldType.link ],
		[ FieldName.invoiceNumber, FieldType.link ],
		[ FieldName.cost, FieldType.currency ],
		[ FieldName.name, FieldType.link ],
		[ FieldName.lastLogin, FieldType.date ],
		[ FieldName.dueDate, FieldType.date ],
		[ FieldName.crmOppNumber, FieldType.text ],
		[ FieldName.billingAddress, FieldType.text || FieldType.address ],
		[ FieldName.quoteId, FieldType.pdf ],
		[ FieldName.creationDate, FieldType.dateString ],
		[ FieldName.dealership, FieldType.dealershipInfo ],
		[ FieldName.dateOfFirstUse, FieldType.dateString ],
		[ 'default', FieldType.text ],
		[ FieldName.endDate, FieldType.dateString ],
		[ FieldName.subscriptionName, FieldType.subscription ],
		[ FieldName.operatorHour, FieldType.operatorHour ],
		[ FieldName.subscriptionPlan, FieldType.subscription ],
		[ FieldName.action, FieldType.action ],
		[ FieldName.subscriptionTerm, FieldType.subscriptionTerm ],
		[ FieldName.locationLabel, FieldType.locationLabel ],
		[ FieldName.equipmentLocation, FieldType.equipmentAddress ],
		[ FieldName.primarySubscriptionAddress, FieldType.primarySubLocation ]

	]);
	private readonly orderStatusMap = new Map<string, string>([
		[ InvoiceStatusHeader.all, '' ],
		[ InvoiceStatusHeader.orderinProcess, OrderStatus.orderProcess ],
		[ InvoiceStatusHeader.inProduction, OrderStatus.orderProduction ],
		[ InvoiceStatusHeader.shipped, OrderStatus.shipped ],
		[ InvoiceStatusHeader.delivered, OrderStatus.deliverStatus ]
	]);
	private readonly myQuotesStatusMap = new Map<string, string>([
		[ MyQuotesStatusHeader.all, '' ],
		[ MyQuotesStatusHeader.readyForReview, StatusPayload.readyForReview ],
		[ MyQuotesStatusHeader.quoteinProcess, StatusPayload.quoteinProcess ],
		[ MyQuotesStatusHeader.quoteAccepted, StatusPayload.quoteAccepted ],
		[ MyQuotesStatusHeader.ordered, StatusPayload.ordered ],
		[ MyQuotesStatusHeader.expired, StatusPayload.expired ]
	]);
	navigateWithCustomerSupport(url:string) 
	{
		if(this.isCustomerSupportSession)
		{
			const userProfile = this.localStorageService.getImpersonateUser();
			
			if(userProfile)
			{
				const user = JSON.parse(userProfile);
				const currentPath = btoa(user.email);
			    window.open(`${url}&email=${currentPath}`, '_blank', 'noopener');
			}
		}
		else 
		{
			window.open(url, '_blank', 'noopener');
		}
	}
	getFieldType(fieldName: string): string
	{
		let result: string | undefined = FieldType.text;
		if (this.fieldMap.has(fieldName))
		{
			result = this.fieldMap.get(fieldName);
		}
		return result?? FieldType.text;
	}
	getOrderStatus(fieldName: string): string
	{
		let result: string | undefined = '';
		if (this.orderStatusMap.has(fieldName))
		{
			result = this.orderStatusMap.get(fieldName);
		}
		return result?? '';
	}
	getMyQuotesStatus(fieldName: string): string
	{
		let result: string | undefined = '';
		if (this.myQuotesStatusMap.has(fieldName))
		{
			result = this.myQuotesStatusMap.get(fieldName);
		}
		return result ?? '';
	}
	statusValueToRedirectsMap: StatusValueToRedirectsMap = new Map<UserRegistrationStatusValue, RoutingFunction>([
		[ 'approved', () =>
		{
			return this.routeToPortalFromOutside('Approved');
		} ],
		[ 'active', () =>
		{
			return this.routeToPortalFromOutside('Active');
		} ],
		[ 'not_approved', (router: Router) =>
		{
			return router.navigate([this.translate.currentLang + '/confirmation']);
		} ],
		[ 'pending_approval', (router: Router) =>
		{
			return router.navigate([this.translate.currentLang + '/confirmation']);
		} ],
		[ 'pending_business_approval', (router: Router) =>
		{
			return router.navigate([this.translate.currentLang + '/pending-approval']);
		} ],
		[ 'in_progress', (router: Router) =>
		{
			return router.navigate([this.translate.currentLang + '/registration']);
		} ],
		[ null, (router: Router) =>
		{
			const isRequestSubmittedForReviewTrue = localStorage.getItem(IsRequestSubmittedForReview.isRequestSubmittedForReview);
			if(isRequestSubmittedForReviewTrue) return router.navigate([this.translate.currentLang + '/confirmation']);
			else return router.navigate([this.translate.currentLang + '/registration']);
		} ]
	]);

	routeToPortalFromOutside(registrationStatus: string)
	{
		const inboundUrlPathName = localStorage.getItem('inboudUrl');
		const lang = this.translate.currentLang || 'en';
		if (inboundUrlPathName !== null && inboundUrlPathName !== '/auth')
		{
			return this.router.navigateByUrl(lang + inboundUrlPathName);
		}
		else
		{
			return this.checkTheRegistrationStatusAndRoute(registrationStatus);
	    }
	}

	checkTheRegistrationStatusAndRoute(registrationStatus: string)
	{
		const lang = this.translate.currentLang || 'en';
		if(registrationStatus === 'Approved')
		{
			return this.router.navigate([lang +'/app']);
		}
		else
		{
			return this.router.navigate([lang +'/confirmation']);
		}
	}

	setImageBasedOnStatusCode(statusCode: number): Image
	{
		let image = { iconPath: '', altText: '' };
		if (statusCode === 401)
		{
			image = {
				iconPath: Icons.unauthorized,
				altText: '401 Unauthorized'
			};
		}
		else if (statusCode === 403)
		{
			image = {
				iconPath: Icons.userNotActivated,
				altText: 'not activated'
			};
		}
		else if (statusCode === 202)
		{
			image = {
				iconPath: Icons.pendingApproval,
				altText: 'pending approval'
			};
		}
		else if (statusCode === 500)
		{
			image = {
				iconPath: Icons.userNotActivated,
				altText: 'internal server error'
			};
		}
		else if (statusCode === 100)
		{
			image = {
				iconPath: Icons.noLocation,
				altText: 'no locations'
			};
		}
		return image;
	}
	handlePendingAndNotApprovedState(responseObject: UserRegistrationStatus)
	{
		if ( responseObject.hasOwnProperty('registrationStatus') &&
			(responseObject.registrationStatus === 'not_approved' ||
			responseObject.registrationStatus === 'pending_approval')
		)
		{
			const message = responseObject.registrationStatus === 'pending_approval' ? 'Pending Approval' : '';
			const statusCode = responseObject.registrationStatus === 'not_approved' ? 402 : 202;
			this.triggerErrorService(message, statusCode, '');
		}
	}
	triggerErrorService(status: string, statusCode: number, id:string)
	{
		const image: Image = this.setImageBasedOnStatusCode(statusCode);
		this.errorService.updateState({ messages: [{ message: status }], statusCode: statusCode, id: id, image: image });
	}
	getHeaderObjectState(isSort: boolean, label: string, sortOrder: SortOrder.asc | SortOrder.desc, hideSort?: boolean, category?:string ):KngHeaderObjState
	{
		const headerObjectState : KngHeaderObjState = { isSort: isSort, label: label, sortOrder: sortOrder };
		if(hideSort)
		{
			headerObjectState.hideSort = hideSort;
		}
		if(category)
		{
			headerObjectState.category = category;
		}
		return headerObjectState;
	};
	getMessage()
	{
		return { text: ErrorMessage.furtherActionsContactMessage, tokens: [] };
	}
	getButtonState(size: KngButtonSize, style: KngButtonStyle, label: string, disabled: boolean, icon: Image, position: KngButtonPosition  ): KngButtonState
	{
		const buttonState : KngButtonState = { size: size, style: style, label: label, disabled: disabled, icon: icon, position: position };
		return buttonState;
	};
	hideModalBackdrop()
	{
		document.body.classList.remove('modal-open');
		document.body.removeAttribute('style');
		const backdrop = document.querySelector('.modal-backdrop');
		backdrop?.remove();
	}
	checkCardState(cardState: KngCardListState)
	{
		if (cardState !== undefined && cardState.list.length > 0)
		{
			cardState.header = [];
			cardState.list = [];
		}
	}
	orderNumberMapping(key: string, value: KngValueObjState, label: string)
	{
		if (key === FieldName.truckNumber)
		{
			value.orderId = label;
		}
	}
	locationIdMapping(key: string, value: KngValueObjState, label: string)
	{
		if (key === FieldName.locationId)
		{
			value.locationId = label;
		}
	}
	public formatValue(value: string, valueType: string): string
	{
		if (value.trim())
		{
			value = this.characterFormat(value, valueType);
		}
		return value;
	}
	characterFormat(value: string, valueType: string)
	{
		if(valueType === FormattingName.companyName || valueType === FormattingName.accountName)
		{
			const company = value.replace(/ /g, ' ');
			return company.trimLeft();
		}
		else if(valueType === FormattingName.city)
		{
			const address = value.replace(/[^a-zA-Z ]/g, '');
			return address;
		}
		else if(valueType === FormattingName.addressOne || valueType === FormattingName.addressTwo)
		{
			const address = value.replace(/ /g, ' ');
			return address;
		}
		return value.trim();
	}
	formatZipCode(value: string, country: string = CountryCode.unitedStatesofAmerica)
	{
		let formattedZip = value;
		if(country === CountryCode.canada || country === CountryCode.canadaCountry)
		{
			value = value.trim();
			if (value.length >= 6)
			{
				value = value.replace(/ /g, '');
				value = value.replace(/-+/g, '');
				value = value.substring(0, 3) + ' ' + value.substring(3, 7);
			}
			formattedZip = value;
		}
		else if(country === CountryCode.unitedStatesofAmerica)
		{
			const zipCode = value.replace(/[^\d]/g, '');
			const zipCodeLength = zipCode.length;
			formattedZip = zipCodeLength < 6 ? zipCode : `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
		}
		return formattedZip;
	}
	getCardListOrangeIcon()
	{
		return { url: Icons.rightArrowOrange, alt: 'next' };
	}
	getCardListAccordionIcon()
	{
		return { url: '', alt: 'down' };
	}
	getCardListGreyIcon()
	{
		return { url: Icons.leftArrow, alt: 'previous' };
	}
	getDefaultErrorObject()
	{
		return {
			message: 'No results found',
			messageSubText: '',
			icon: Icons.noResult
		};
	}
	getLastTenErrorObject()
	{
		return {
			message: ErrorMessage.lastTenErrorcodesEmptyData,
			messageSubText: '',
			icon: Icons.noErrorCodes
		};

	}
	getStateOnRemoveReason(selectedReason: string, commentAdded: string): boolean
	{
		if (selectedReason && (this.removeReasons.includes(selectedReason)))
		{
			return false;
		}
		else
		{
			return this.soldExportSelected(selectedReason, commentAdded);
		}
	}
	soldExportSelected(selectedReason: string, commentAdded: string): boolean
	{
		if (selectedReason && (this.commentRequiredRemoveReasons.includes(selectedReason)) && commentAdded)
		{
			return false;
		}
		else
		{
			return true;
		}
	}


	getRoutingFunction(userResponse: UserRegistrationStatus): RoutingFunction | undefined
	{
		const isTmhCustomerSupportRole = this.localStorageService.getCustomerToDashboard();

		this.checkAccountManager(userResponse);

		if (this.isCustomerSupportUser(userResponse, isTmhCustomerSupportRole))
		{
		  return this.getCustomerSupportRoutingFunction();
		}

		if (this.isTmhCorporateAdminUser(userResponse, isTmhCustomerSupportRole))
		{
		  return this.getCorporateAdminRoutingFunction();
		}

		if (isTmhCustomerSupportRole)
		{
		  return this.getCustomerSupportRoleRoutingFunction();
		}

		return this.getStatusBasedRoutingFunction(userResponse);
	}

	private checkAccountManager(userResponse: UserRegistrationStatus): void
	{
		if (this.isAccountManager(userResponse))
		{
		  this.isAccountManagerCredential = true;
		}
	}

	private getCustomerSupportRoutingFunction(): RoutingFunction
	{
		return (router: Router) => router.navigate([this.translate.currentLang + '/app' + ApplicationPath.customerSupport]);
	}

	private getCorporateAdminRoutingFunction(): RoutingFunction
	{
		return (router: Router) => router.navigate([this.translate.currentLang + '/app' + ApplicationPath.customerportal]);
	}
	private getCustomerSupportRoleRoutingFunction(): RoutingFunction
	{
		return () => this.routeToPortalFromOutside('Approved');
	}
	private getStatusBasedRoutingFunction(userResponse: UserRegistrationStatus): RoutingFunction | undefined
	{
		if (userResponse.hasOwnProperty('registrationStatus') && userResponse.registrationStatus !== undefined)
		{
		  return this.statusValueToRedirectsMap.get(userResponse.registrationStatus);
		}
		return undefined;
	  }
	isTmhCorporateAdminRole(role: string[])
	{
		return !!role.includes(tmhCorporateAdminRoleId);
	}
	isCustomerSupportRole(role: string[])
	{
		return !!role.includes(tmhCustomerSupportRoleId);
	}
	isTmhCorporateAdminUser(responseObject: UserRegistrationStatus, isTmhCustomerSupportRole: boolean = false)
	{
		return !!(responseObject.roles && this.isTmhCorporateAdminRole(responseObject.roles)  && !isTmhCustomerSupportRole);
	}
	isCustomerSupportUser(responseObject: UserRegistrationStatus, isTmhCustomerSupportRole: boolean = false)
	{
		return !!(responseObject.roles && this.isCustomerSupportRole(responseObject.roles) && !isTmhCustomerSupportRole );
	}
	isAccountManagerRole(role: string[])
	{
		return !!role.includes(accountManagerRoleId);
	}
	isAccountManager(responseObject: UserRegistrationStatus)
	{
		return !!(responseObject.roles && this.isAccountManagerRole(responseObject.roles));
	}
	async isLoggedInUserPresent(): Promise<void>
	{
		return new Promise<void>(resolve =>
		{
			const checkLocalStorage = () =>
			{
				if (this.isAnyUserPresent())
				{
					this.updateUserRoles();
					resolve();
				}
				else
				{
					setTimeout(checkLocalStorage, 100);
				}
			};
			checkLocalStorage();
		});
	}

	private isAnyUserPresent(): boolean
	{
		return !!(this.localStorageService.getTmhCorporateAdminDetails() ||
			   this.localStorageService.getLoggedInUser() ||
			   this.localStorageService.getTmhCustomerSupportDetails());
	}
	private updateUserRoles(): void
	{
		if (this.localStorageService.getTmhCorporateAdminDetails())
		{
			this.isTmhCorporateAdmin$.next(true);
		}
		if (this.localStorageService.getTmhCustomerSupportDetails())
		{
			this.isTmhCustomerSupportRole$.next(true);
		}
	}
	handleRoutingFunction(routingFunction : RoutingFunction | undefined, router: Router)
	{
		if(routingFunction)
		{
			routingFunction(router)?.then(succeeded =>
			{
				if(succeeded)
				{
					this.loggerService.log('navigation succeeded');
				}
			})
				.catch(error =>
				{
					this.loggerService.error(error);
				});
		}
	}

	updateIsMobileView(): boolean
	{
  	const isMobile = window.innerWidth <= MaxDeviceWidth.mobile;
  	return isMobile;
	}

	setLoaderBasedOnCardState(cardstate: KngCardListState) 
	{
		if (cardstate !== undefined && cardstate.list.length > 0)
			cardstate.loading = true;
	
		return cardstate;
	}
}

